import React, { useState, useEffect } from 'react';
import { Check, Plus, Trash2, RefreshCw, AlertCircle, ListOrdered } from 'lucide-react';
import { projectAPI, interactiveCardsAPI } from '../services/api';

const PlanUpdateCard = ({ card, projectId, messageId, handledCardIds = [], autoDismissedCardIds = new Set(), cardStates = {}, onUpdateComplete, className = '', demoClicking = false }) => {
  const [loading, setLoading] = useState(false);
  
  // Use demoClicking as external loading state if provided
  const isLoading = loading || demoClicking;
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Check if card is already handled (persisted state from database) or auto-dismissed (local state)
  useEffect(() => {
    // ISSUE 3 FIX: Check both handledCardIds (persisted) and autoDismissedCardIds (local state)
    const isInHandledCards = card?.id && handledCardIds.includes(card.id);
    const isAutoDismissed = card?.id && autoDismissedCardIds && autoDismissedCardIds.has && autoDismissedCardIds.has(card.id);
    
    if (isAutoDismissed) {
      // Immediately mark as dismissed from local state
      setDismissed(true);
      return;
    }
    
    if (isInHandledCards) {
      // Card was previously handled - check specific state from message metadata
      const cardState = cardStates[card.id];
      // Handle both string state and object state (e.g., { state: 'auto_dismissed' })
      const stateValue = typeof cardState === 'object' ? cardState?.state : cardState;
      
      if (stateValue === 'completed') {
        setCompleted(true);
      } else if (stateValue === 'dismissed' || stateValue === 'auto_dismissed') {
        // ISSUE 3 FIX: Treat auto_dismissed same as dismissed - silently mark as done
        setDismissed(true);
      } else {
        // Fallback to localStorage (for cards handled before this update)
        const storedState = localStorage.getItem(`plan_update_${card.id}_state`);
        if (storedState === 'completed') {
          setCompleted(true);
        } else if (storedState === 'dismissed') {
          setDismissed(true);
        } else {
          // Default to completed if handled but no specific state
          setCompleted(true);
        }
      }
    }
  }, [card?.id, handledCardIds, autoDismissedCardIds, cardStates]);

  // The proposal array comes from the backend response
  const proposal = card.proposal || [];

  const handleConfirm = async () => {
    if (!projectId) {
        setError("Missing Project ID");
        return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        changes: proposal,
        objectivesSignature: card.objectives_signature || card.objectivesSignature || null
      };
      await projectAPI.batchUpdateObjectives(projectId, payload);
      setCompleted(true);
      
      // Mark card as handled in database for persistence
      if (messageId && card?.id) {
        try {
          await interactiveCardsAPI.markHandled({ messageId, cardId: card.id, state: 'completed' });
          // Also store in localStorage as backup
          localStorage.setItem(`plan_update_${card.id}_state`, 'completed');
        } catch (err) {
          console.warn("Failed to mark plan update card as handled:", err);
          // Don't fail the whole operation if this fails
        }
      }
      
      if (onUpdateComplete) {
        try {
          onUpdateComplete({
            type: 'plan_update_applied',
            projectId,
            messageId,
            cardId: card?.id
          });
        } catch (_) {}
      }
    } catch (err) {
      console.error("Failed to update plan:", err);
      if (err?.response?.status === 409) {
        setError('Plan changed in the background. Please review the latest objectives before applying.');
      } else {
      setError(err.message || 'Failed to update plan');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    setDismissed(true);
    
    // Mark card as handled in database for persistence
    if (messageId && card?.id) {
      try {
        await interactiveCardsAPI.markHandled({ messageId, cardId: card.id, state: 'dismissed' });
        // Also store in localStorage as backup
        localStorage.setItem(`plan_update_${card.id}_state`, 'dismissed');
      } catch (err) {
        console.warn("Failed to mark plan update card as dismissed:", err);
        // Don't fail the whole operation if this fails
      }
    }
  };

  if (completed) {
    return (
      <div className={`bg-white dark:bg-gray-800 border border-emerald-300 dark:border-emerald-700 rounded-2xl overflow-hidden ${className}`}>
        <div className="flex min-h-[56px]">
          {/* Icon stripe */}
          <div className="relative w-16 min-h-full overflow-hidden flex-shrink-0">
            <div className="absolute inset-0 w-full h-full bg-blue-100 dark:bg-blue-900/30 rounded-l-2xl flex items-center justify-center">
              <RefreshCw size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 py-4 px-4 sm:px-5 flex items-center min-w-0">
            <div className="flex items-center justify-between w-full gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[15px] sm:text-base font-semibold leading-tight text-gray-900 dark:text-white">
                  Plan updated
                </div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  {proposal.length} change{proposal.length !== 1 ? 's' : ''} applied
                </div>
              </div>
              <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                <Check size={12} className="text-white" strokeWidth={3} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (dismissed) {
    return (
      <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden ${className}`}>
        <div className="flex min-h-[56px]">
          {/* Icon stripe */}
          <div className="relative w-16 min-h-full overflow-hidden flex-shrink-0">
            <div className="absolute inset-0 w-full h-full bg-gray-100 dark:bg-gray-700 rounded-l-2xl flex items-center justify-center">
              <RefreshCw size={20} className="text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 py-4 px-4 sm:px-5 flex items-center min-w-0">
            <div className="flex items-center justify-between w-full gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[15px] sm:text-base font-semibold leading-tight text-gray-500 dark:text-gray-400">
                  Plan update dismissed
                </div>
              </div>
              <button 
                onClick={() => setDismissed(false)}
                className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1.5 transition-colors px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <RefreshCw size={12} />
                <span>Undo</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!proposal || proposal.length === 0) {
      return null;
  }

  return (
    <div className={`relative overflow-hidden dark:bg-gray-800 bg-white border border-gray-200 dark:border-gray-700 rounded-2xl ${className}`}>
      {/* Header row with icon pattern (matching other cards) */}
      <div className="flex items-start gap-5 sm:gap-6 p-4">
        {/* Icon column - consistent width like other cards */}
        <div className="flex-shrink-0">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900/50 dark:to-blue-800/30 text-blue-600 dark:text-blue-400 shadow-sm">
            <RefreshCw size={22} strokeWidth={2.5} />
          </div>
        </div>
        
        {/* Title/subtitle */}
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] sm:text-base font-semibold leading-tight text-gray-900 dark:text-gray-100">
            {card.title || 'Review Changes'}
          </h3>
          {card.subtitle && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-3">{card.subtitle}</p>
          )}
        </div>
      </div>
      
      {/* Proposal items */}
      <div className="px-4 pb-4 space-y-2">
        {proposal.map((change, idx) => (
          <div key={idx} className="flex items-start gap-3 text-sm p-3 bg-gray-50/50 dark:bg-gray-700/40 rounded-xl">
            <div className="mt-0.5 flex-shrink-0">
              {change.action === 'create' && <Plus size={16} className="text-green-500" />}
              {change.action === 'delete' && <Trash2 size={16} className="text-red-500" />}
              {change.action === 'update' && <RefreshCw size={16} className="text-blue-500" />}
              {change.action === 'reorder' && <ListOrdered size={16} className="text-orange-500" />}
            </div>
            <div className="flex-1 min-w-0">
              {change.action === 'create' && (
                <div className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-green-700 dark:text-green-400">Add:</span> {change.name}
                  {change.description && <div className="text-xs text-gray-500 mt-0.5">{change.description}</div>}
                </div>
              )}
              {change.action === 'delete' && (
                <div className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-red-700 dark:text-red-400">Remove:</span> {change.name || `Objective`}
                </div>
              )}
              {change.action === 'update' && (
                <div className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-blue-700 dark:text-blue-400">Update:</span> {change.name || 'Objective'}
                  {change.status && <span className="text-xs ml-2 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 rounded text-blue-700 dark:text-blue-300 uppercase tracking-wider font-medium">{change.status}</span>}
                </div>
              )}
              {change.action === 'reorder' && (
                <div className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-orange-700 dark:text-orange-400">Reorder List</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="px-4 pb-3 text-sm text-red-600 flex items-center gap-2">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Action buttons - primary left, secondary right */}
      <div className="px-4 pb-4 pt-1 flex gap-3">
        <button
          onClick={handleConfirm}
          disabled={isLoading}
          className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-3 rounded-xl font-bold text-[15px] sm:text-base flex items-center justify-center gap-2 disabled:opacity-50 transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.98] min-h-[48px]"
        >
          {isLoading ? <RefreshCw size={18} className="animate-spin" /> : <Check size={18} />}
          {isLoading ? 'Applying...' : 'Apply Changes'}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          disabled={isLoading}
          className="px-5 py-3 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

export default PlanUpdateCard;

